/**
 * Lets administrators save the current workspace as reusable configuration.
 *
 * Template creation is separated from general settings so its asynchronous
 * copy semantics and success/error feedback do not complicate ordinary
 * workspace edits or imply that user data is part of the template.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input } from '@/components/ui'
import { getWorkspacesApi } from '@/subsystems/workspaces'
import { ORGANIZATIONS_QUERY_KEY } from '@/subsystems/workspaces/hooks/use-organizations'
import { applicationKeys } from '@/subsystems/workspaces/services/templates-api'

interface SaveAsTemplateResponse {
  templateId: string
  name: string
}

export function WorkspaceTemplateSettings({
  orgId,
  workspaceId,
}: {
  orgId: string
  workspaceId: string
}) {
  const queryClient = useQueryClient()
  // Templates remain workspace-owned configuration. Keeping this operation in
  // Advanced prevents Vault sharing controls from implying template access.
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async () => (
      await getWorkspacesApi().post<SaveAsTemplateResponse>(
        `/workspaces/${orgId}/${workspaceId}/save-as-template`,
        { name: name.trim() },
      )
    ).data,
    onSuccess: async result => {
      // cm-coding: Saving a workspace template can create the template-library
      // organization and changes the wizard catalog. Invalidate every public
      // read model before reporting success so subsequent navigation cannot
      // hide the template behind a five-minute React Query cache.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['org-workspaces', '_templates_'] }),
        queryClient.invalidateQueries({ queryKey: applicationKeys.all }),
      ])
      setName('')
      setError(null)
      toast.success(`"${result.name}" saved to template library`)
    },
    onError: (cause: unknown) => {
      const response = (cause as {
        response?: { status?: number; data?: { detail?: string } }
      }).response
      if (response?.status === 409) {
        setError(response.data?.detail ?? 'A template with this name already exists')
        return
      }
      toast.error(response?.data?.detail ?? 'Failed to create template')
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Copy className="h-4 w-4" />
          Save as Template
        </CardTitle>
        <CardDescription>
          Copy this workspace into the template library. Documents and analysis results are not included.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={name}
            onChange={event => {
              setName(event.target.value)
              setError(null)
            }}
            placeholder="Template name"
            aria-label="Template name"
            maxLength={100}
            disabled={save.isPending}
            className={error ? 'border-destructive' : undefined}
          />
          <Button
            disabled={save.isPending || !name.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save as Template
          </Button>
        </div>
        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}
